import { PageHero } from "@/components/PageHero";
import React, { useState } from "react";
import { api, getSessionId } from "@/lib/api";
import { MessageSquareHeart, CheckCircle2, Star, ThumbsUp, ThumbsDown, HelpCircle, ExternalLink, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getParticipantId, buildCompletionUrl, markFeedbackSubmitted } from "@/lib/userInterviews";
import { getProlificContext, isProlificTester, PROLIFIC_COMPLETION_URL } from "@/lib/prolific";
import { track, EVENTS } from "@/lib/analytics";

/* -------------------------------------------------------------------------- */
/*  The 5 questions that matter — easy chips + short text                      */
/* -------------------------------------------------------------------------- */

const USE_AGAIN_OPTIONS = [
    { value: "yes", label: "Yes", Icon: ThumbsUp, tone: "bg-stage_early text-stage_early-dark", activeTone: "bg-stage_early-dark text-white" },
    { value: "maybe", label: "Maybe", Icon: HelpCircle, tone: "bg-yellow-soft text-yellow-dark", activeTone: "bg-yellow-dark text-white" },
    { value: "no", label: "No", Icon: ThumbsDown, tone: "bg-coral-light text-coral-dark", activeTone: "bg-coral-dark text-white" },
];

const HELPFUL_FEATURES = [
    "Talk to Lane (AI chat)",
    "Burnout check-in",
    "Roadmap",
    "Hard conversations",
    "Patient profile",
    "Behavior tracker",
    "Resources",
    "Appointment prep",
    "Family communication",
    "Just having a place for this",
];

const FEELING_TAGS = [
    "Calmer",
    "Less alone",
    "Hopeful",
    "Validated",
    "Seen",
    "Understood",
    "Relieved",
    "Equipped",
    "Overwhelmed",
    "Frustrated",
    "Sad",
    "Skeptical",
];

const PMF_OPTIONS = [
    { value: "very", label: "Very disappointed", tone: "bg-stage_early text-stage_early-dark", activeTone: "bg-stage_early-dark text-white" },
    { value: "somewhat", label: "Somewhat disappointed", tone: "bg-yellow-soft text-yellow-dark", activeTone: "bg-yellow-dark text-white" },
    { value: "not", label: "Not disappointed", tone: "bg-coral-light text-coral-dark", activeTone: "bg-coral-dark text-white" },
    { value: "na", label: "Not applicable", tone: "bg-cream-dark text-[#4C5551]", activeTone: "bg-[#4C5551] text-white" },
];

const RETURN_FREQ_OPTIONS = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "A few times a week" },
    { value: "monthly", label: "A few times a month" },
    { value: "rarely", label: "Only when something hard comes up" },
    { value: "never", label: "I don't see myself coming back" },
];

export default function Feedback() {
    const sid = getSessionId();
    const participantId = getParticipantId();
    // Snapshot the Prolific context once at mount so the submission carries
    // the exact STUDY_ID + SESSION_ID the participant arrived with — even
    // if they wander to another tab and storage gets garbage-collected.
    const prolificCtx = React.useMemo(() => getProlificContext(), []);
    const isProlific = React.useMemo(() => isProlificTester(), []);
    const internalCompletionPath = buildCompletionUrl(participantId);

    // Instrumentation — log "feedback_page_viewed" once per mount so the
    // Prolific Diagnostics view can see the participant reached this step.
    // Fire-and-forget; never blocks the page or surfaces errors.
    React.useEffect(() => {
        if (!isProlific || !participantId) return;
        api.post("/prolific/track-step", {
            step: "feedback_page_viewed",
            participant_id: participantId,
            study_id: prolificCtx.studyId,
            session_id: prolificCtx.sessionId,
        }).catch(() => {});
    }, [isProlific, participantId, prolificCtx.studyId, prolificCtx.sessionId]);

    const [useAgain, setUseAgain] = useState("");
    const [helpful, setHelpful] = useState([]);
    const [helpfulOther, setHelpfulOther] = useState("");
    const [confusing, setConfusing] = useState("");
    const [feelings, setFeelings] = useState([]);
    const [wouldMiss, setWouldMiss] = useState("");
    const [pmf, setPmf] = useState("");
    const [returnFreq, setReturnFreq] = useState("");
    const [womScore, setWomScore] = useState(null);
    const [womFirstPerson, setWomFirstPerson] = useState("");
    const [email, setEmail] = useState("");
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(0);
    // Server-confirmed redirect target. Only populated AFTER the
    // feedback row lands in mongo — the participant cannot reach Prolific
    // via the success UI until the server has acknowledged the save and
    // returned the live completion_url for the active study.
    const [verifiedRedirect, setVerifiedRedirect] = useState({ url: "", code: "" });
    // Last error from the submit call, surfaced as a Retry banner instead
    // of a one-off toast so the participant can't accidentally lose their
    // response by clicking away.
    const [submitError, setSubmitError] = useState("");

    const toggleHelpful = (v) => {
        setHelpful((h) => (h.includes(v) ? h.filter((x) => x !== v) : [...h, v]));
    };
    const toggleFeeling = (v) => {
        setFeelings((f) => (f.includes(v) ? f.filter((x) => x !== v) : [...f, v]));
    };

    const hasAnything = !!(
        useAgain ||
        helpful.length ||
        helpfulOther.trim() ||
        confusing.trim() ||
        feelings.length ||
        wouldMiss.trim() ||
        pmf ||
        returnFreq ||
        womScore !== null ||
        womFirstPerson.trim()
    );

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) {
            toast.error("Please tap a star to give Memory Lane an overall rating.");
            return;
        }
        if (!hasAnything) {
            toast.error("Add at least one note or chip — even a single word helps.");
            return;
        }
        setSubmitting(true);
        setSubmitError("");
        try {
            // Send the FULL Prolific context (pid + study_id + session_id +
            // source) so the row carries the routing data the server uses
            // to (a) snapshot the right study_config and (b) emit the
            // structured prolific_events audit log.
            const resp = await api.post("/feedback", {
                session_id: sid,
                rating,
                category: "Caregiver pulse",
                message: wouldMiss || confusing || helpfulOther || "",
                email,
                participant_id: participantId,
                prolific_pid: participantId,
                prolific_study_id: prolificCtx.studyId || "",
                prolific_session_id: prolificCtx.sessionId || "",
                source: isProlific ? "prolific" : (participantId ? "user_interviews" : "web"),
                would_use_again: useAgain,
                most_helpful: helpful,
                most_helpful_other: helpfulOther,
                confusing_or_unnecessary: confusing,
                made_me_feel: feelings,
                would_miss_most: wouldMiss,
                pmf_disappointment: pmf,
                wom_likelihood: womScore,
                wom_first_person: womFirstPerson,
                return_frequency: returnFreq,
            });
            // Transactional: only show success + arm redirect AFTER the
            // server confirms the row landed. The redirect URL comes from
            // the server response, NOT a client cache — so even if the
            // founder swaps Prolific studies mid-deploy, the participant
            // is redirected to the code that was attached to THIS row.
            const data = resp?.data || {};
            if (!data.ok || !data.id) {
                throw new Error("Server didn't confirm save.");
            }
            setVerifiedRedirect({
                url: data.completion_url || PROLIFIC_COMPLETION_URL,
                code: data.completion_code || "",
            });
            setDone(true);
            markFeedbackSubmitted(participantId);
            track(EVENTS.FEEDBACK_SUBMITTED, {
                rating,
                would_use_again: useAgain,
                pmf: pmf,
                wom_score: womScore,
            });
            // Tester accounts are single-use — once survey is in, the backend
            // suspends the account so the /tester/?iid=... link can't be reused.
            // /feedback already auto-suspends when participant_id matches a
            // tester; this call covers any tester signed-in without an iid.
            try { await api.post("/auth/tester-complete"); } catch (_e) { /* not a tester / already done */ }
            // Arm the auto-redirect for the friendly /study-complete page
            // (which uses useStudyConfig() for its own Prolific button).
            if (internalCompletionPath) {
                setRedirectCountdown(4);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail
                || err?.message
                || "Couldn't send. Please try again.";
            setSubmitError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const retrySubmit = () => {
        setSubmitError("");
        // Re-invoke through a fake event so we re-run the same submit pipeline
        // without the user having to scroll back up to the Submit button.
        submit({ preventDefault: () => { } });
    };

    // Tick the countdown then bounce to the internal /study-complete page
    // (which uses the live server study-config to render the verified
    // Prolific "Return to Prolific" button — no client-cached URL).
    React.useEffect(() => {
        if (redirectCountdown <= 0) return undefined;
        if (redirectCountdown === 1) {
            const t = setTimeout(() => {
                window.location.href = internalCompletionPath;
            }, 1000);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => setRedirectCountdown((n) => n - 1), 1000);
        return () => clearTimeout(t);
    }, [redirectCountdown, internalCompletionPath]);

    return (
        <div className="space-y-8 animate-fade-in-up max-w-2xl mx-auto">
            <PageHero
                testId="feedback-header"
                kicker="Feedback"
                kickerIcon={MessageSquareHeart}
                title="How is Memory Lane working for you?"
                subtitle={<>Eight gentle questions. Skip anything that doesn&apos;t apply — every word shapes what we build next.</>}
            />
            <header>
                <div
                    data-testid="feedback-impact-banner"
                    className="ml-card p-4 bg-stage_late-light/60 !border-stage_late-light"
                >
                    <p className="text-sm text-[#1F2624] leading-relaxed">
                        <strong>Why this matters:</strong> the daughter Googling at 2 AM for how to handle her dad&apos;s
                        wandering. The son who can&apos;t get his sister on the same page about Mom&apos;s care. They get
                        a better tool because of the next 3 minutes you spend here. Be brutally honest — kindly or
                        fiercely — both help.
                    </p>
                </div>
            </header>

            {!done && (
                <form onSubmit={submit} className="space-y-6" data-testid="feedback-form">
                    {/* Required overall star rating */}
                    <section className="ml-card p-6 md:p-7 bg-cream-dark/40 !border-cream-dark" data-testid="fb-rating">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">
                            Overall rating <span className="text-coral-dark not-italic">*</span>
                        </p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            How would you rate Memory Lane today?
                        </label>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    data-testid={`feedback-star-${n}`}
                                    onMouseEnter={() => setHover(n)}
                                    onMouseLeave={() => setHover(0)}
                                    onClick={() => setRating(rating === n ? 0 : n)}
                                    aria-label={`Rate ${n} stars`}
                                    className="p-1.5 transition-transform hover:scale-110"
                                >
                                    <Star
                                        size={36}
                                        strokeWidth={1.5}
                                        className={
                                            (hover || rating) >= n
                                                ? "fill-[#E6B400] text-[#E6B400]"
                                                : "text-[#CBD5E0]"
                                        }
                                    />
                                </button>
                            ))}
                            {rating > 0 && (
                                <span className="ml-2 text-sm font-medium text-[#4C5551]" data-testid="feedback-rating-label">
                                    {[
                                        "",
                                        "Not for me",
                                        "Could be better",
                                        "It's okay",
                                        "Really helpful",
                                        "Couldn't do this without it",
                                    ][rating]}
                                </span>
                            )}
                        </div>
                    </section>

                    {/* Q1 — Would you use Memory Lane again? */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q1">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 1 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            Would you use Memory Lane again?
                        </label>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {USE_AGAIN_OPTIONS.map((opt) => {
                                const active = useAgain === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        data-testid={`fb-use-again-${opt.value}`}
                                        onClick={() => setUseAgain(active ? "" : opt.value)}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-medium border transition-all ${active
                                                ? `${opt.activeTone} border-transparent shadow-sm`
                                                : `${opt.tone} border-transparent hover:opacity-80`
                                            }`}
                                    >
                                        <opt.Icon size={16} strokeWidth={1.8} />
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Q2 — Most helpful feature? */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q2">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 2 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            What was the most helpful feature?
                        </label>
                        <p className="mt-1 text-sm text-[#6E7A74]">Tap any that fit — or write your own below.</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {HELPFUL_FEATURES.map((f) => {
                                const active = helpful.includes(f);
                                return (
                                    <button
                                        key={f}
                                        type="button"
                                        data-testid={`fb-helpful-${f.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                                        onClick={() => toggleHelpful(f)}
                                        className={`px-3.5 py-1.5 rounded-2xl text-sm font-medium border transition-colors ${active
                                                ? "bg-teal text-white border-teal"
                                                : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                            }`}
                                    >
                                        {f}
                                    </button>
                                );
                            })}
                        </div>
                        <input
                            type="text"
                            data-testid="fb-helpful-other"
                            value={helpfulOther}
                            onChange={(e) => setHelpfulOther(e.target.value)}
                            placeholder="Something else? (optional)"
                            className="ml-input mt-4"
                        />
                    </section>

                    {/* Q3 — Confusing / unnecessary */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q3">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 3 of 8</p>
                        <label htmlFor="fb-confusing" className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            What was confusing or unnecessary?
                        </label>
                        <p className="mt-1 text-sm text-[#6E7A74]">Be honest — kindly or fiercely. Both help.</p>
                        <textarea
                            id="fb-confusing"
                            data-testid="fb-confusing"
                            value={confusing}
                            onChange={(e) => setConfusing(e.target.value)}
                            className="ml-input mt-4 min-h-[100px] py-3"
                            placeholder="A section that felt off, a step that didn't make sense, a button that took you somewhere you didn't expect…"
                        />
                    </section>

                    {/* Q4 — How did it make you feel? */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q4">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 4 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            How did using Memory Lane make you feel?
                        </label>
                        <p className="mt-1 text-sm text-[#6E7A74]">Pick any that fit — the good and the not-so-good.</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {FEELING_TAGS.map((f) => {
                                const active = feelings.includes(f);
                                return (
                                    <button
                                        key={f}
                                        type="button"
                                        data-testid={`fb-feeling-${f.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                                        onClick={() => toggleFeeling(f)}
                                        className={`px-3.5 py-1.5 rounded-2xl text-sm font-medium border transition-colors ${active
                                                ? "bg-stage_late-dark text-white border-stage_late-dark"
                                                : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                            }`}
                                    >
                                        {f}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Q5 — Sean Ellis PMF test */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q5">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 5 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            How would you feel if you could no longer use Memory Lane?
                        </label>
                        <p className="mt-1 text-sm text-[#6E7A74]">There&apos;s no right answer — your honest gut reaction is what we&apos;re after.</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {PMF_OPTIONS.map((opt) => {
                                const active = pmf === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        data-testid={`fb-pmf-${opt.value}`}
                                        onClick={() => setPmf(active ? "" : opt.value)}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-medium border transition-all ${active
                                            ? `${opt.activeTone} border-transparent shadow-sm`
                                            : `${opt.tone} border-transparent hover:opacity-80`
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Q6 — Return frequency */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q6">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 6 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            Realistically, how often do you see yourself opening Memory Lane?
                        </label>
                        <div className="mt-4 flex flex-col gap-2">
                            {RETURN_FREQ_OPTIONS.map((opt) => {
                                const active = returnFreq === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        data-testid={`fb-return-${opt.value}`}
                                        onClick={() => setReturnFreq(active ? "" : opt.value)}
                                        className={`text-left px-4 py-2.5 rounded-2xl border font-medium transition-colors ${active
                                            ? "bg-teal text-white border-teal"
                                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Q7 — Word-of-mouth + name capture */}
                    <section className="ml-card p-6 md:p-7" data-testid="fb-q7">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Question 7 of 8</p>
                        <label className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            In the next 7 days, how likely are you to tell another caregiver about Memory Lane?
                        </label>
                        <p className="mt-1 text-sm text-[#6E7A74]">0 = not at all · 10 = I&apos;ll text someone tonight.</p>
                        <div className="mt-4 grid grid-cols-11 gap-1.5" data-testid="fb-wom-scale">
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                                const active = womScore === n;
                                return (
                                    <button
                                        key={n}
                                        type="button"
                                        data-testid={`fb-wom-${n}`}
                                        onClick={() => setWomScore(active ? null : n)}
                                        aria-label={`Score ${n}`}
                                        className={`h-11 rounded-xl font-medium text-sm border transition-all tabular-nums ${active
                                            ? "bg-teal-dark text-white border-teal-dark shadow-sm"
                                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                            }`}
                                    >
                                        {n}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-4">
                            <label htmlFor="fb-wom-first" className="ml-label">
                                Who&apos;s the first person you&apos;d tell? <span className="text-[#6E7A74] font-normal">(name + their relationship to you)</span>
                            </label>
                            <input
                                id="fb-wom-first"
                                type="text"
                                data-testid="fb-wom-first"
                                value={womFirstPerson}
                                onChange={(e) => setWomFirstPerson(e.target.value)}
                                className="ml-input"
                                placeholder="e.g. My cousin Maria — her dad just got diagnosed"
                            />
                        </div>
                    </section>

                    {/* Q8 — Would miss most */}
                    <section className="ml-card p-6 md:p-7 bg-teal-soft !border-teal-soft" data-testid="fb-q8">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">Question 8 of 8</p>
                        <label htmlFor="fb-miss" className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">
                            If Memory Lane disappeared tomorrow, what would you miss most?
                        </label>
                        <textarea
                            id="fb-miss"
                            data-testid="fb-miss"
                            value={wouldMiss}
                            onChange={(e) => setWouldMiss(e.target.value)}
                            className="ml-input mt-4 min-h-[100px] py-3 !bg-white"
                            placeholder="A feature, a feeling, a specific moment…"
                        />
                    </section>

                    {/* Optional extras — collapsed under a small heading */}
                    <details className="ml-card p-5 md:p-6" data-testid="fb-extras">
                        <summary className="text-sm font-medium text-[#4C5551] cursor-pointer hover:text-teal-dark">
                            Want us to follow up? Leave your email (optional)
                        </summary>
                        <div className="mt-4">
                            <label className="ml-label" htmlFor="fb-email">Email (only if you want a reply)</label>
                            <input
                                id="fb-email"
                                type="email"
                                data-testid="feedback-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="ml-input"
                                placeholder="you@example.com"
                            />
                        </div>
                    </details>

                    {/* Inline retry banner — never silently lose a participant's
                        work. If the server returned an error, surface it here
                        with a Retry button so they can resubmit without
                        rewriting their answers. */}
                    {submitError && (
                        <div
                            data-testid="feedback-submit-error"
                            className="ml-card p-4 bg-coral-light/40 border-coral-light flex items-start gap-3"
                        >
                            <AlertTriangle size={18} strokeWidth={1.8} className="text-coral-dark shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-coral-dark">We couldn&apos;t save your feedback yet.</p>
                                <p className="text-xs text-[#4C5551] mt-1 leading-relaxed">{submitError}</p>
                                <p className="text-[11px] text-[#6E7A74] mt-1">
                                    Your answers are still on this page — nothing is lost.
                                </p>
                                <button
                                    type="button"
                                    onClick={retrySubmit}
                                    disabled={submitting}
                                    data-testid="feedback-retry-submit"
                                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-coral-dark text-white text-xs font-medium hover:bg-coral-dark/90 disabled:opacity-50"
                                >
                                    {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
                                    Retry
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Submit */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-[#6E7A74]">
                            Thank you for taking the time. You&apos;re shaping this for every caregiver who comes next.
                        </p>
                        <button
                            type="submit"
                            disabled={submitting || !rating || !hasAnything}
                            data-testid="feedback-submit"
                            className="ml-btn-primary disabled:opacity-50"
                        >
                            {submitting ? "Sending…" : "Send feedback"}
                        </button>
                    </div>
                </form>
            )}

            {done && (
                <div className="ml-card p-8 md:p-10 text-center bg-ml_blue-light !border-ml_blue-light" data-testid="feedback-success">
                    <CheckCircle2 size={44} strokeWidth={1.5} className="text-teal mx-auto mb-3" />
                    <h2 className="text-2xl md:text-3xl font-heading font-medium text-teal">Thank you.</h2>
                    <p className="mt-2 text-[#1F2624]">Your words go directly to the team building Memory Lane.</p>
                    <p className="mt-3 text-sm text-[#4C5551] max-w-md mx-auto leading-relaxed">
                        Because of what you just shared, the next caregiver who lands here in the
                        middle of a crisis will find a kinder, smarter, more useful tool. That&apos;s a
                        real gift to a real person.
                    </p>

                    {internalCompletionPath && (
                        <div
                            data-testid="feedback-ui-redirect"
                            className="mt-6 ml-card p-4 bg-white/70 !border-stage_late text-left max-w-md mx-auto"
                        >
                            <p className="text-sm text-[#1F2624]">
                                <strong>Wrapping things up…</strong>
                            </p>
                            <p className="text-xs text-[#4C5551] mt-1">
                                Sending you to a quick thank-you page in <span data-testid="feedback-ui-countdown">{Math.max(redirectCountdown, 1)}</span>s — or
                                <a
                                    href={internalCompletionPath}
                                    data-testid="feedback-ui-finish-now"
                                    className="text-teal-dark underline hover:text-teal ml-1 inline-flex items-center gap-1"
                                >
                                    go now <ExternalLink size={11} strokeWidth={1.8} />
                                </a>.
                            </p>
                            {verifiedRedirect.code && (
                                <p className="mt-2 text-[11px] text-[#6E7A74]" data-testid="feedback-verified-code">
                                    Completion code attached to your submission:{" "}
                                    <code className="font-mono text-teal-dark">{verifiedRedirect.code}</code>
                                </p>
                            )}
                        </div>
                    )}

                    <button
                        onClick={() => {
                            setDone(false);
                            setUseAgain("");
                            setHelpful([]);
                            setHelpfulOther("");
                            setConfusing("");
                            setFeelings([]);
                            setWouldMiss("");
                            setPmf("");
                            setReturnFreq("");
                            setWomScore(null);
                            setWomFirstPerson("");
                            setEmail("");
                            setRating(0);
                        }}
                        data-testid="feedback-again"
                        className="ml-btn-secondary mt-6"
                    >
                        Send another note
                    </button>
                </div>
            )}
        </div>
    );
}
